import { AdminPageLayout } from '@/components/admin/AdminPageLayout';
import { ScheduleControls } from '@/components/admin/schedule-visualizer/ScheduleControls';
import { ScheduleDisplay } from '@/components/admin/schedule-visualizer/ScheduleDisplay';
import { Card, CardContent } from '@/components/ui/card';
import { trpc } from '@/utils/trpc';
import { useSuperAdmin } from '@/utils/use-super-admin';
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router';
import { format } from 'date-fns';
import { FileSearch, Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { z } from 'zod';

const scheduleVisualizerSearchSchema = z.object({
  orgId: z.string().optional(),
  providerId: z.string().optional(),
  facilityId: z.string().optional(),
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});

export const Route = createFileRoute('/_protected/admin/schedule-visualizer')({
  component: ScheduleVisualizer,
  validateSearch: scheduleVisualizerSearchSchema,
});

function ScheduleVisualizer() {
  const { isSuperAdmin, isLoading: isAdminLoading } = useSuperAdmin();
  const router = useRouter();
  const navigate = useNavigate();
  const search = Route.useSearch();

  // Initialize from URL params or defaults
  const selectedOrgId = search.orgId || '';
  const selectedProviderId = search.providerId || '';
  const selectedFacilityId = search.facilityId || '';
  const selectedDate = search.date || format(new Date(), 'yyyy-MM-dd');

  useEffect(() => {
    if (!isAdminLoading && !isSuperAdmin) {
      router.navigate({ to: '/dashboard' });
    }
  }, [isAdminLoading, isSuperAdmin, router]);

  // Update URL params helper - removes empty strings for cleaner URLs
  const updateParams = (updates: Partial<z.infer<typeof scheduleVisualizerSearchSchema>>) => {
    navigate({
      to: '.',
      search: (prev) => {
        const newSearch = { ...prev, ...updates };
        // Remove empty string values for cleaner URLs
        for (const key in newSearch) {
          if (newSearch[key as keyof typeof newSearch] === '') {
            delete newSearch[key as keyof typeof newSearch];
          }
        }
        return newSearch;
      },
    });
  };

  // Fetch organizations
  const { data: organizations, isLoading: orgsLoading } = trpc.debug.getOrganizations.useQuery(
    undefined,
    { enabled: isSuperAdmin }
  );

  // Fetch providers when organization is selected
  const { data: providers, isLoading: providersLoading } = trpc.debug.getProviders.useQuery(
    { organizationId: selectedOrgId },
    { enabled: !!selectedOrgId && isSuperAdmin }
  );

  // Fetch facilities when organization is selected
  const { data: facilities, isLoading: facilitiesLoading } = trpc.debug.getFacilities.useQuery(
    { organizationId: selectedOrgId },
    { enabled: !!selectedOrgId && isSuperAdmin }
  );

  // Fetch schedule when all required parameters are present (facility is optional)
  const { data: schedule, isLoading: scheduleLoading } = trpc.debug.getScheduleDebug.useQuery(
    {
      organizationId: selectedOrgId,
      providerId: selectedProviderId,
      date: selectedDate,
      facilityId: selectedFacilityId || undefined,
    },
    { enabled: !!selectedOrgId && !!selectedProviderId && !!selectedDate && isSuperAdmin }
  );

  if (isAdminLoading || orgsLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!isSuperAdmin) {
    return null;
  }

  // Get selected facility name for display
  const selectedFacilityName = facilities?.find((f) => f.id === selectedFacilityId)?.name;

  return (
    <AdminPageLayout
      title="Schedule Visualizer"
      description="Debug tool for viewing synced schedules from the database"
      icon={FileSearch}
      isSuperAdminPage={true}
    >
      <div className="flex flex-col gap-6">
        <ScheduleControls
          organizations={organizations || []}
          providers={providers || []}
          facilities={facilities || []}
          selectedOrgId={selectedOrgId}
          selectedProviderId={selectedProviderId}
          selectedFacilityId={selectedFacilityId}
          selectedDate={selectedDate}
          onOrgChange={(orgId) => {
            updateParams({ orgId, providerId: '', facilityId: '' });
          }}
          onProviderChange={(providerId) => {
            updateParams({ providerId });
          }}
          onFacilityChange={(facilityId) => {
            updateParams({ facilityId });
          }}
          onDateChange={(date) => {
            updateParams({ date });
          }}
          providersLoading={providersLoading}
          facilitiesLoading={facilitiesLoading}
          scheduleLoading={scheduleLoading}
        />

        {scheduleLoading && (
          <Card>
            <CardContent className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </CardContent>
          </Card>
        )}

        {schedule && !scheduleLoading && (
          <ScheduleDisplay
            scheduleData={schedule}
            selectedDate={selectedDate}
            selectedFacilityName={selectedFacilityName}
          />
        )}
      </div>
    </AdminPageLayout>
  );
}
